import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, ArrowUpRight, Contact } from "lucide-react";
import { Link } from "react-router-dom";
import useScrollToTop from "@/hooks/useScrollToTop";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { DASHBOARD_SIGNIN_URL } from "@/config/urls";

const pricingPlans = [
  {
    name: "YouAux Plus",
    price: "$5.99",
    description: "Perfect for emerging artists starting their journey",
    features: [
      "Keep 85% of your Earnings",
      "Unlimited Releases to all major DSPs",
      "Unlimited Artist Accounts",
      "Unlimited UPcs Daily",
      "Own Custom Labels",
      "Meta Content ID",
      "TikTok Content ID",
      "Pre-Save Link Creation",
      "Lyrics to Apple Music",
      "Free ISRC & UPC/EAN Codes",
      "Daily Streaming Analytics Dashboard",
      "Rights Administration & Royalties Collection",
      "7 days submission",
      "Monetization Tools for FREE",
      "Royalty Splits at Source",
      "Country Blocking", 
      "Neighbouring Rights Collection",
      "Customize your prices on Amazon and iTunes",
      "Music Sales Reports",
      "Worldwide Platforms",
      "Compilation Releases",
      "Global Bank Transfer Payout",
      "Standard Payouts",
      "Lifetime Release Guarantee",
      "Standard Support",
    ],
  },
  {
    name: "YouAux Pro",
    price: "$23.99",
    description: "For active artists looking to accelerate their growth and reach",
    features: [
      "Keep 90% of your Earnings",
      "Every Feature of YouAux Plus",
      "YouTube Content ID",
      "YouTube Topic Mapping",
      "Instagram Profile Mapping",
      "SoundCloud Profile Mapping",
      "Minimum Payout Threshold - €30",
      "Spotify Discorvery Mode",
      "Beatport Access",
      "3 Priority Releases within 3 days per month",
      "AI Cover Art Generation",
      "AI Lyrics Generation",
      "Advanced analytics & reporting",
      "Guidance under YouAux Team",
      "YouAux Social Media & Playlist Promotion",
      "Release Protection",
      "5 Days Submission",
      "Fast Payouts",
      "Priority Support",
      "Access to All Upcoming Features",
    ],
    recommended: true
  },
  {
    name: "YouAux Premium",
    price: "$56.99",
    description: "For active artists building their career in Worldwide",
    features: [
      "Keep 95% of your Earnings",
      "Every Feature of YouAux Pro",
      "1 Official Artist Channel - Free",
      "Facebook Whitelist",
      "Bulk Distribution",
      "WARM Radio Tracking (20% off)",
      "Dedicated Account Manager",
      "CMS Service(selected users)",
      "Free VEVO Distribution (selected users)",
      "Access of funding (under YouAux Select)",
      "Record Label Platform (on YouTube)",
      "Artist Promotion",
      "Playlist Pitching Service",
      "Priority Release Scheduling (within 3 days)",
      "Custom Marketing Campaign Report",
      "Ultra-Fast Payouts",
      "Live Chat Support",
      "24/7 Support",
    ],
  }
];

const comparisonFeatures = [
  { name: "Earnings Split", basic: "85%", pro: "90%", premium: "95%" },
  { name: "Analytics", basic: "Daily Streaming Analytics", pro: "Advanced Analytics & Reporting", premium: "Advanced Analytics & Reporting" },
  { name: "Release Time", basic: "7 days", pro: "5 days + 3 Priority Releases within 3 days per month", premium: "Priority Release (within 3 days)" },
  { name: "Support Response Time", basic: "3 Business Days", pro: "2 Business Days", premium: "1 Business Day" },
  { name: "YouTube Content ID", basic: "Not Included", pro: "YouTube Content ID", premium: "YouTube Content ID" },
  { name: "Beatport Access", basic: "Not Included", pro: "Beatport Access", premium: "Beatport Access" },
  { name: "Marketing", basic: "Pre-Save Link", pro: "Spotify Discovery Mode", premium: "Custom Marketing Campaign Report + Playlist Pitching" },
  { name: "Distribution", basic: "Major DSPs", pro: "Major DSPs + VEVO Distribution", premium: "Major DSPs + WARM Radio Tracking" },
  { name: "Additional Services", basic: "Basic Features", pro: "Guidance under YouAux Team", premium: "CMS Service + Publishing Admin + Facebook Whitelist" },
  { name: "Artist Channel", basic: "Not Included", pro: "Not Included", premium: "Free Official Artist Channel" },
  { name: "Payouts", basic: "Standard Payouts", pro: "Fast Payouts", premium: "Ultra-Fast Payouts" },
  { name: "Rights Collection", basic: "Neighbouring Rights Collection", pro: "Neighbouring Rights Collection", premium: "Neighbouring Rights Collection" }
];

const Pricing = () => {
  useScrollToTop();
  const [isIndian, setIsIndian] = useState(false);
  const SIGNIN_URL = DASHBOARD_SIGNIN_URL;
  const loading = false;
  
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const locResponse = await fetch("https://geolocation-db.com/json/");
        const locData = await locResponse.json();
        setIsIndian(locData.country_code === "IN");
      } catch (error) {
        console.error("Error detecting location:", error);
      }
    };

    detectLocation();
  }, []);

  const handleBuyNow = () => {
    window.location.href = SIGNIN_URL;
  };

  const formatPrice = (usdPrice: string) => {
    if (usdPrice === "Custom") return "Custom";
    if (usdPrice === "$5.99") return isIndian ? "₹499" : "$5.99";
    if (usdPrice === "$23.99") return isIndian ? "₹1999" : "$23.99";
    if (usdPrice === "$56.99") return isIndian ? "₹4999" : "$56.99";
    return usdPrice;
  };

  // Helper: parse and format monthly/yearly prices while billing yearly
  const getYearlyAmount = (usdPrice: string) => {
    if (usdPrice === "Custom") return null;
    if (usdPrice === "$0") return 0;
    // For Indian users we map USD list prices to INR yearly totals
    if (isIndian) {
      if (usdPrice === "$5.99") return 499;
      if (usdPrice === "$23.99") return 1999;
      if (usdPrice === "$56.99") return 4999;
      // Fallback: try to parse numeric USD and return as-is (will be treated as INR later if needed)
      const parsed = Number(usdPrice.replace(/[^0-9.]/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    }
    // Non-Indian users: use the USD value directly
    const parsed = Number(usdPrice.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatCurrency = (amount: number) => {
    const symbol = isIndian ? "₹" : "$";
    // Keep two decimals for precision so that 1999/12 isn't misleading
    const isWhole = Math.abs(amount - Math.round(amount)) < 1e-9;
    const value = isWhole ? Math.round(amount).toString() : amount.toFixed(2);
    return `${symbol}${value}`;
  };

  const formatMonthlyFromYearly = (usdPrice: string) => {
    const yearly = getYearlyAmount(usdPrice);
    if (yearly === null) return "Custom";
    // If INR mapping, yearly is INR; else it's USD
    const monthly = yearly / 12;
    return formatCurrency(monthly);
  };

  const formatYearlyDisplay = (usdPrice: string) => {
    const yearly = getYearlyAmount(usdPrice);
    if (yearly === null) return "Custom";
    return formatCurrency(yearly);
  };

  const getPlanButton = () => {
    return (
      <Button 
        onClick={handleBuyNow}
        className="w-full border border-olyve-beige/20 bg-transparent hover:bg-olyve-beige/20 hover:text-white rounded-xl hover:border-olyve-beige text-cream-100 transition-all duration-300"
        disabled={loading}
      >
        Get Started
      </Button>
    );
  };

  return (
    <>
      <Helmet>
        <title>Pricing | YouAux</title>
        <meta name="description" content="Choose the perfect plan for your music career. No hidden fees, keep 100% ownership, and reach millions of listeners worldwide." />
        <link rel="canonical" href="https://youaux.com/pricing" />
      </Helmet>
    <div className="olive-bg olive-grid min-h-screen">
      <Navbar />
      <main className="container mx-auto px-6 md:px-10 py-28">
        <section className="text-left mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-cream-200 font-playfair">
            Pricing
          </h1>
          <p className="text-muted-foreground max-w-2xl text-lg">
            Choose the perfect plan for your music career. No hidden fees, keep 100% ownership, and reach millions of listeners worldwide.
          </p>
        </section>

        {/* Sale Banner */}
        {false && (<section className="mb-10">
          <div className="relative overflow-visible rounded-2xl sale-banner-animated republic-banner-bg backdrop-blur-md shadow-xl p-6 md:p-8">
            <div className="absolute inset-0 overflow-hidden pointer-events-none flag-wave-layer">
              <svg
                className="flag-wave-svg"
                viewBox="0 0 1600 400"
                preserveAspectRatio="xMidYMid slice"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="flag-saffron" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffb366" stopOpacity="0.78" />
                    <stop offset="100%" stopColor="#ff9933" stopOpacity="0.92" />
                  </linearGradient>
                  <linearGradient id="flag-white" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.72" />
                    <stop offset="100%" stopColor="#eaf3ff" stopOpacity="0.82" />
                  </linearGradient>
                  <linearGradient id="flag-green" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4bb065" stopOpacity="0.84" />
                    <stop offset="100%" stopColor="#138808" stopOpacity="0.9" />
                  </linearGradient>
                </defs>
                <path
                  className="flag-wave-path flag-wave-path--saffron"
                  d="M0 90 C 200 40 360 130 560 90 C 760 35 980 150 1180 78 C 1380 16 1500 130 1600 60 L1600 0 L0 0 Z"
                  fill="url(#flag-saffron)"
                />
                <path
                  className="flag-wave-path flag-wave-path--white"
                  d="M0 210 C 200 160 360 250 560 210 C 760 160 980 250 1180 190 C 1380 130 1500 250 1600 185 L1600 75 L0 75 Z"
                  fill="url(#flag-white)"
                />
                <path
                  className="flag-wave-path flag-wave-path--green"
                  d="M0 350 C 200 300 360 390 560 350 C 760 300 980 390 1180 330 C 1380 270 1500 390 1600 320 L1600 400 L0 400 Z"
                  fill="url(#flag-green)"
                />
                <g className="flag-ashoka" transform="translate(800 200)">
                  <circle r="34" fill="none" stroke="#0a3c8a" strokeWidth="6" opacity="0.8" />
                  <circle r="10" fill="#0a3c8a" opacity="0.9" />
                  <g stroke="#0a3c8a" strokeWidth="4" strokeLinecap="round">
                    <line y1="-34" y2="34" />
                    <line x1="-34" x2="34" />
                    <line x1="-24" y1="-24" x2="24" y2="24" />
                    <line x1="-24" y1="24" x2="24" y2="-24" />
                    <line x1="-10" y1="-33" x2="10" y2="33" />
                    <line x1="-10" y1="33" x2="10" y2="-33" />
                    <line x1="-33" y1="-10" x2="33" y2="10" />
                    <line x1="-33" y1="10" x2="33" y2="-10" />
                    <line x1="-28" y1="-19" x2="28" y2="19" />
                    <line x1="-28" y1="19" x2="28" y2="-19" />
                    <line x1="-19" y1="-28" x2="19" y2="28" />
                    <line x1="-19" y1="28" x2="19" y2="-28" />
                  </g>
                </g>
              </svg>
            </div>
            {/* Decorative art */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Floral corners */}
              <svg className="sale-corner sale-corner--tl" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18 70c8-12 16-18 26-20-6-8-6-16 0-24 6-7 14-9 24-6 1-10 7-16 16-18-3 10-2 18 4 24 8 6 16 6 24-2-2 8-8 14-18 16 3 10 1 18-6 24-8 6-16 6-24 0-2 10-8 18-20 26 2-10 0-18-6-24-6-6-14-8-20-6Z" stroke="rgba(211,204,177,0.6)" strokeWidth="2" fill="rgba(211,204,177,0.12)"/>
                <circle cx="54" cy="30" r="4" fill="rgba(211,204,177,0.5)"/>
                <circle cx="76" cy="44" r="3" fill="rgba(211,204,177,0.45)"/>
                <circle cx="60" cy="60" r="3" fill="rgba(211,204,177,0.35)"/>
              </svg>
              <svg className="sale-corner sale-corner--br" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M102 50c-8 12-16 18-26 20 6 8 6 16 0 24-6 7-14 9-24 6-1 10-7 16-16 18 3-10 2-18-4-24-8-6-16-6-24 2 2-8 8-14 18-16-3-10-1-18 6-24 8-6 16-6 24 0 2-10 8-18 20-26-2 10 0 18 6 24 6 6 14 8 20 6Z" stroke="rgba(211,204,177,0.6)" strokeWidth="2" fill="rgba(211,204,177,0.12)"/>
                <circle cx="66" cy="90" r="4" fill="rgba(211,204,177,0.5)"/>
                <circle cx="44" cy="76" r="3" fill="rgba(211,204,177,0.45)"/>
                <circle cx="60" cy="60" r="3" fill="rgba(211,204,177,0.35)"/>
              </svg>

              {/* Floral stars near CTA */}
              <svg className="sale-star sale-star--cta2" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M50 8 L58 28 L80 20 L72 40 L92 50 L72 60 L80 80 L58 72 L50 92 L42 72 L20 80 L28 60 L8 50 L28 40 L20 20 L42 28 Z" stroke="rgba(211,204,177,0.6)" strokeWidth="2" fill="rgba(211,204,177,0.10)"/>
                <circle cx="50" cy="50" r="5" fill="rgba(211,204,177,0.45)"/>
                <circle cx="68" cy="34" r="2.5" fill="rgba(211,204,177,0.35)"/>
                <circle cx="34" cy="68" r="2.5" fill="rgba(211,204,177,0.35)"/>
              </svg>
              <svg className="sale-star sale-star--cta" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M50 6 L60 26 L82 18 L74 40 L94 50 L74 60 L82 82 L60 74 L50 94 L40 74 L18 82 L26 60 L6 50 L26 40 L18 18 L40 26 Z" stroke="rgba(211,204,177,0.65)" strokeWidth="2.2" fill="rgba(211,204,177,0.12)"/>
                <circle cx="50" cy="50" r="6" fill="rgba(211,204,177,0.55)"/>
                <path d="M50 20 V80" stroke="rgba(211,204,177,0.28)" strokeWidth="2" strokeLinecap="round"/>
                <path d="M20 50 H80" stroke="rgba(211,204,177,0.28)" strokeWidth="2" strokeLinecap="round"/>
              </svg>

              {/* Fireworks / confetti */}
              <svg className="sale-art sale-art--left sale-art--sparkle" width="210" height="120" viewBox="0 0 210 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <g stroke="rgba(211,204,177,0.55)" strokeWidth="2" strokeLinecap="round">
                  <path d="M38 55 L20 40" />
                  <path d="M38 55 L16 62" />
                  <path d="M38 55 L28 78" />
                  <path d="M38 55 L52 78" />
                  <path d="M38 55 L60 62" />
                  <path d="M38 55 L58 40" />
                  <path d="M38 55 L38 30" />
                  <path d="M38 55 L38 86" />
                </g>
                <g fill="rgba(211,204,177,0.35)">
                  <circle cx="88" cy="32" r="3" />
                  <circle cx="110" cy="58" r="2.5" />
                  <circle cx="95" cy="78" r="2.5" />
                  <circle cx="145" cy="36" r="2.2" />
                  <circle cx="165" cy="62" r="2.2" />
                  <circle cx="150" cy="84" r="2.2" />
                </g>
                <g stroke="rgba(211,204,177,0.35)" strokeWidth="2" strokeLinecap="round">
                  <path d="M132 62 L152 40" />
                  <path d="M132 62 L158 62" />
                  <path d="M132 62 L152 84" />
                </g>
              </svg>
            </div>

            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs tracking-wider border border-olyve-beige/40 text-olyve-beige">
                    Limited Time
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs tracking-wider border border-olyve-beige/20 text-cream-100/80">
                    Ends Jan 28, 2026
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-cream-200 sale-title-em">
                  YouAux Republic Day Sale
                </h2>
                <p className="mt-2 text-cream-100/80 text-base md:text-lg">
                  Get <span className="text-red-500 font-extrabold attention-pulse">10% off</span> on all YouAux subscriptions from{" "}
                  <span className="text-olyve-beige font-bold">25 January, 2026</span> to <span className="text-olyve-beige font-bold">28 January, 2026</span>. No coupon required.
                </p>
              </div>
              <div className="shrink-0">
                <Link to="#plans">
                  <Button className="rounded-full px-6 py-3 bg-transparent border border-olyve-beige/40 text-cream-100 hover:bg-olyve-beige/10 hover:text-white hover:border-olyve-beige transition-all duration-300">
                    Explore Plans
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>)}

        <section id="plans" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {pricingPlans.map((plan, index) => (
            <div 
              key={index} 
              className={`
                relative overflow-hidden group h-full flex flex-col
                ${plan.recommended 
                  ? 'border-2 border-olyve-beige bg-black/40 backdrop-blur-xl shadow-[0_0_30px_rgba(211,204,177,0.2)]' 
                  : 'border-2 border-border/50 bg-black/20 backdrop-blur-lg shadow-[0_0_20px_rgba(147,39,143,0.3)]'
                } 
                transition-all duration-500 p-8 rounded-2xl
              `}
            >
              {plan.recommended && (
                <div className="absolute top-0 right-0 bg-olyve-beige text-black px-4 py-1">
                {/* <div className="absolute top-0 right-0 bg-olyve-beige text-black "> */}
                  Most Popular
                </div>
              )}
              
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-4xl font-bold mb-3 text-white font-comic-sans">{plan.name}</h3>
                  <p className="text-muted-foreground text-base leading-relaxed">{plan.description}</p>
                </div>
                <ArrowUpRight className="text-olyve-beige opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              <div className="mb-8">
                {plan.name === "YouAux Basic" ? (
                  <>
                    <div className="text-5xl font-bold text-cream-200 mb-2">
                      {formatPrice(plan.price)}
                    </div>
                    {plan.price !== "Custom" && (
                      <span className="text-sm text-muted-foreground">/ Forever</span>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2 mb-1">
                      <div className="text-5xl font-bold text-cream-200">
                        {formatMonthlyFromYearly(plan.price)}
                      </div>
                      <span className="text-lg md:text-xl text-muted-foreground font-semibold">/ month</span>
                    </div>
                    <span className="text-xs text-muted-foreground/80">
                      billed yearly {formatYearlyDisplay(plan.price)}
                    </span>
                  </>
                )}
              </div>

              <ul className="space-y-4 mb-8 flex-1">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-base text-muted-foreground group-hover:text-cream-200/90 transition-colors duration-300">
                    <Check size={20} className={`${index === 0 || index === 2 ? 'text-purple-400' : 'text-olyve-beige'} flex-shrink-0 mt-1`} />
                    <span className="leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {getPlanButton()}
            </div>
          ))}
        </section>

        <section className="mb-20">
          <h2 className="text-3xl font-bold mb-6 text-cream-200">Compare Plans</h2>
          <p className="text-muted-foreground max-w-2xl text-lg mb-8">
            Find the perfect plan for your needs. Compare features across YouAux Plus and YouAux Premium plans.
          </p>
          
          <div className="overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b border-border/50 hover:bg-transparent transition-colors">
                  <TableHead className="text-white font-bold text-lg md:text-xl border-r border-border/30 relative">
                    <span>Features</span>
                    <span aria-hidden className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-6 w-px bg-gradient-to-b from-transparent via-white/25 to-transparent"></span>
                  </TableHead>
                  <TableHead className="text-white text-center font-bold text-lg md:text-xl border-r border-border/30 relative">
                    <div>YouAux Plus</div>
                    <span aria-hidden className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-6 w-px bg-gradient-to-b from-transparent via-white/25 to-transparent"></span>
                  </TableHead>
                  <TableHead className="text-white text-center font-bold text-lg md:text-xl border-r border-border/30 relative">
                    <div>YouAux Pro</div>
                    <span aria-hidden className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-6 w-px bg-gradient-to-b from-transparent via-white/25 to-transparent"></span>
                  </TableHead>
                  <TableHead className="text-white text-center font-bold text-lg md:text-xl">YouAux Premium</TableHead>
                </TableRow>
               {/* <TableRow className="hover:bg-transparent"> */}
                 <TableHead colSpan={4} className="p-0">
                   <div className="h-px w-full bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                 </TableHead>
               {/* </TableRow> */}
              </TableHeader>
              <TableBody>
                <TableRow className="border-b border-border/50 hover:bg-transparent transition-colors">
                  <TableCell className="font-medium text-[15px] text-white border-r border-border/20 relative">
                    <span>Price</span>
                    <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                  </TableCell>
                  <TableCell className="text-center text-olyve-beige font-bold text-[16px] border-r border-border/20 relative">
                    {formatPrice("$5.99")}
                    <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                  </TableCell>
                  <TableCell className="text-center text-olyve-beige font-bold text-[16px] border-r border-border/20 relative">
                    {formatPrice("$23.99")}
                    <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                  </TableCell>
                  <TableCell className="text-center text-olyve-beige font-bold text-[16px]">{formatPrice("$56.99")}</TableCell>
                </TableRow>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={index} className="border-b border-border/50 hover:bg-transparent transition-colors">
                    <TableCell className="font-medium text-[14px] text-white border-r border-border/20 relative">
                      {feature.name}
                      <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground border-r border-border/20 relative">
                      {feature.basic}
                      <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground border-r border-border/20 relative">
                      {feature.pro}
                      <span aria-hidden className="hidden md:block absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/20 to-transparent"></span>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">{feature.premium}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <section className="text-center">
          <h2 className="text-3xl font-bold mb-4 text-cream-200">Still Not Sure?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Our team is here to help you choose the right plan for your music career.
            Contact us for a personalized consultation.
          </p>
          <Link to="/contact">
            <Button className="olive-button transition-all duration-300 rounded-xl hover:scale-105 hover:shadow-glow">
              <Contact size={20} className="mr-2" /> Talk to Our Team
            </Button>
          </Link>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default Pricing;
